import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse, AxiosError,
  InternalAxiosRequestConfig
} from 'axios';
import { BaseError } from 'viem';
import { Env } from '@/libs/env';
import { findLocale } from '@/libs/find-locale';
import { getFormattedIpLocation } from '@/features/auth/services/ip-location';
import { getAccessToken, getDeviceInfo, isInApp, type DeviceInfo } from './comeAppSDK';
import { getTranslation } from '@/utils/get-translation';
import toast from 'react-hot-toast';
import { safeCaptureException } from '@/utils/sentry-utils';

export const IgnoreError = Symbol('Prevent error message to show');

// 基础响应类型
export interface ErrorResponse {
  code?: number;
  msg?: string;
}

// 扩展响应类型，包含数据和基础响应字段
export type ApiResponse<T = any> = T & ErrorResponse;

// 缓存deviceInfo
let deviceInfo: DeviceInfo;

export class HttpResponseError extends Error {
  public response: AxiosResponse;

  constructor(error: string, response: AxiosResponse) {
    super(error);
    this.name = 'HttpResponseError';
    this.response = response;
    Object.setPrototypeOf(this, HttpResponseError.prototype);
  }
}

const createHttpInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: `${Env.NEXT_PUBLIC_HTTP_SERVER}` || '/api',
    timeout: 10000,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
      if (typeof window !== 'undefined') {
        let token = '';

        if (isInApp()) {
          token = await getAccessToken();
        } else {
          token = localStorage.getItem('accessToken') ?? '';
        }

        if (token) {
          config.headers.set('Authorization', `${token}`);
        }

        const ipLocation = await getFormattedIpLocation();
        if (ipLocation) {
          config.headers.set('X-Ip-Country', ipLocation?.country || '');
          config.headers.set('X-Region', ipLocation?.country || '');
        }

        const pathname = window.location.pathname;
        const lang = findLocale(pathname);

        config.headers.set('X-Lang', lang);

        // 统一插入包名
        try {
          if (isInApp()) {
            if (!deviceInfo) {
              deviceInfo = await getDeviceInfo();
            }
            config.headers.set('X-Package-Name', deviceInfo?.appId || '');
          }
          // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (e) {
        }
      }
      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor (保持简化:仅抛出错误,由react-query onError处理)
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      const { config, data } = response;

      const ignoreError = config.params?.[IgnoreError] === true;
      const { code, msg } = data;
      // 后端非 401 之类的错误 作为 data 的 code 来处理
      if (`${code}`.length > 3 && !(data instanceof BaseError)) {
        console.warn(`[${config.method?.toUpperCase()}] ${config.url} - ${code}, ${msg}`);

        safeCaptureException(new Error(`HTTP ${code}: ${msg}`), {
          tags: {
            'http.method': config.method?.toUpperCase() || 'UNKNOWN',
            'http.url': config.url || '',
            'error.code': code,
          },
          level: 'warning',
        });

        return {
          data: {
            code,
            msg,
          }
        };
      }

      if (code !== 0 && !ignoreError) {
        throw new HttpResponseError(msg || 'Unknown error', response);
      }
      return data;
    },
    async (error: AxiosError) => {
      // 只在客户端显示 toast
      if (typeof window !== 'undefined') {
        // 处理 500 服务器错误，显示 toast 提示
        if (error.response?.status === 500) {
          const errorMessage = await getTranslation('common.105004');
          toast(errorMessage);
        }
      }
      return Promise.reject(error);
    }
  );

  return instance;
};

const http = createHttpInstance();

// 返回Promises，兼容@tanstack/react-query queryFn
export const get = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  const response = await http.get<ApiResponse<T>>(url, config);
  return response.data;
};

export const post = async <T = any>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  const response = await http.post<ApiResponse<T>>(url, data, config);
  return response.data;
};

export const del = async <T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> => {
  const response = await http.delete<ApiResponse<T>>(url, config);
  return response.data;
};